import React from 'react';
import { Box, Container, Typography, Stack, Button, Divider } from '@mui/material';
import Grid from '@mui/material/Grid';
import DashboardIcon from '@mui/icons-material/Dashboard';
import GavelIcon from '@mui/icons-material/Gavel';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BadgeIcon from '@mui/icons-material/Badge';
import MapIcon from '@mui/icons-material/Map';
import BlurOnIcon from '@mui/icons-material/BlurOn';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import AutoDeleteIcon from '@mui/icons-material/AutoDelete';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ShieldIcon from '@mui/icons-material/Shield';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BusinessIcon from '@mui/icons-material/Business';
import Header from './Header';
import Footer from './Footer';
import SEO from './SEO';
import { useNavigate, useLocation } from 'react-router-dom';
import { trackEvent } from './analytics';

const personas = [
  {
    icon: <DashboardIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />,
    role: 'VP of Operations',
    benefit: 'One alert dashboard across all locations. You know about an incident before your regional managers do — every room, every shift, every facility.',
  },
  {
    icon: <GavelIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />,
    role: 'Risk & Compliance',
    benefit: 'Legal-grade timestamped clips. Full audit trail. Regulatory documentation for every incident — chain-of-custody metadata that holds up in court.',
  },
  {
    icon: <AccountBalanceIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />,
    role: 'CFO',
    benefit: 'One settlement costs more than a decade of SMCLA. A single A&M claim that reaches court starts at $500K. Mid-size chains already spend $5,000–$18,000/year just on insurance — SMCLA is what keeps that number from going up.',
  },
  {
    icon: <BadgeIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />,
    role: 'HR Director',
    benefit: 'Protect good staff from false accusations with tamper-evident evidence. Document every incident for disciplinary proceedings before memories fade.',
  },
  {
    icon: <MapIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />,
    role: 'Regional Manager',
    benefit: 'Real-time push alerts to your phone. No more reviewing 8 hours of footage to find 30 seconds that matter. Act before it escalates.',
  },
];

const privacyPillars = [
  {
    icon: <BlurOnIcon sx={{ color: '#5ce1e6', fontSize: 24 }} />,
    title: 'All faces blurred',
    body: "Children's faces are blurred in every alert clip before it leaves the facility. You see the behavior, not the identifiable face.",
  },
  {
    icon: <NotificationsActiveIcon sx={{ color: '#5ce1e6', fontSize: 24 }} />,
    title: 'Flagged clips only',
    body: 'You never receive bulk footage. Only the specific moments that triggered an alert are sent to authorized personnel.',
  },
  {
    icon: <AutoDeleteIcon sx={{ color: '#5ce1e6', fontSize: 24 }} />,
    title: 'Auto-deletion',
    body: 'Clips are automatically deleted after the validation window closes, unless held for an active investigation or legal proceeding.',
  },
  {
    icon: <FactCheckIcon sx={{ color: '#5ce1e6', fontSize: 24 }} />,
    title: 'Full audit trail',
    body: 'Every clip access is logged: who viewed it, when, and why. Chain of custody that holds up in court.',
  },
  {
    icon: <ManageSearchIcon sx={{ color: '#ff9f43', fontSize: 24 }} />,
    title: 'Incident investigation & reconstruction',
    body: '24-hour rolling footage buffer across all cameras. One confirmed alert creates a full investigation package — every angle, every room, forensic-grade export ready for law enforcement or legal proceedings.',
  },
];

const cameraLogos = ['Axis', 'Hanwha', 'Hikvision', 'Bosch', 'Dahua', 'Uniview'];

export default function EnterprisePage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const scrollToContact = () => { trackEvent('cta_click', 'Request an Enterprise Demo', pathname); navigate('/contact'); };

  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <SEO title="Enterprise Childcare Safety — Multi-Site AI Monitoring" description="Built for childcare chains with 5 to 500+ locations. One dashboard, real-time alerts across every facility, and a forensic-grade investigation layer." path="/enterprise" />
      <Header onBookDemo={scrollToContact} />

      {/* Hero */}
      <Box sx={{ position: 'relative', py: { xs: 10, md: 16 }, px: 3, textAlign: 'center', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: '-10%', left: '50%', transform: 'translateX(-50%)', width: 700, height: 700, borderRadius: '50%', background: 'radial-gradient(circle, rgba(92,225,230,0.08) 0%, transparent 70%)', filter: 'blur(60px)', pointerEvents: 'none' }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(92,225,230,0.08)', border: '1px solid rgba(92,225,230,0.2)', borderRadius: 10, px: 2, py: 0.75, mb: 3 }}>
            <BusinessIcon sx={{ color: '#5ce1e6', fontSize: 14 }} />
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 500, fontSize: 12, color: '#5ce1e6', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Enterprise & Multi-Site
            </Typography>
          </Box>

          <Typography variant="h1" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '2.2rem', md: '4rem' }, letterSpacing: '-0.03em', color: '#f0f0f8', mb: 2, lineHeight: 1.05 }}>
            Your cameras see everything.<br />
            <Box component="span" sx={{ color: '#5ce1e6' }}>Only SMCLA understands it.</Box>
          </Typography>

          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: { xs: 16, md: 20 }, color: '#9499b0', maxWidth: 620, mx: 'auto', lineHeight: 1.65, mb: 6 }}>
            For childcare chains with 5 to 500+ locations. SMCLA turns your existing camera infrastructure into a real-time safety intelligence network — at a fraction of the cost of human monitoring.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              onClick={scrollToContact}
              sx={{ px: 4, py: 1.5, fontWeight: 700, fontSize: 16, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, boxShadow: '0 0 24px rgba(92,225,230,0.25)', '&:hover': { bgcolor: '#4ecfd4' } }}
            >
              Request an Enterprise Demo →
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => { const el = document.getElementById('how-it-works'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}
              sx={{ px: 4, py: 1.5, fontWeight: 600, fontSize: 15, color: '#9499b0', borderColor: 'rgba(148,153,176,0.3)', borderRadius: 2, '&:hover': { borderColor: '#9499b0', bgcolor: 'rgba(255,255,255,0.03)' } }}
            >
              See how it works ↓
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Stats bar */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 5, md: 7 }, px: 3 }}>
        <Container maxWidth="md">
          <Grid container spacing={0}>
            {[
              { stat: '1 dashboard', label: 'All locations, unified' },
              { stat: '< 60 sec', label: 'From incident to alert' },
              { stat: '~10× cheaper', label: 'Than human tape review' },
            ].map(({ stat, label }, i) => (
              <Grid key={stat} size={{ xs: 12, md: 4 }}>
                <Box sx={{
                  textAlign: 'center',
                  py: { xs: 3, md: 4 },
                  px: 3,
                  borderRight: { md: i < 2 ? '1px solid rgba(255,255,255,0.06)' : 'none' },
                  borderBottom: { xs: i < 2 ? '1px solid rgba(255,255,255,0.06)' : 'none', md: 'none' },
                }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '2rem', md: '2.5rem' }, color: '#5ce1e6', lineHeight: 1, mb: 1 }}>
                    {stat}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0' }}>
                    {label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Brand risk section */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2 }}>
            <WarningAmberIcon sx={{ color: '#ff9f43', fontSize: 20 }} />
            <Typography variant="overline" sx={{ color: '#ff9f43', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em' }}>
              Brand Risk
            </Typography>
          </Box>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.8rem' }, color: '#f0f0f8', letterSpacing: '-0.03em', mb: 3, lineHeight: 1.1 }}>
            One incident. Every location.
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: { xs: 16, md: 18 }, color: '#9499b0', lineHeight: 1.7, mb: 5, maxWidth: 680 }}>
            A single abuse case at one facility triggers national media coverage, parent trust collapse, and enrollment drops across your entire chain — not just at that site. For a multi-location operator, the reputational math is brutally asymmetric.
          </Typography>

          <Box sx={{ p: { xs: 3, md: 4 }, bgcolor: '#13131f', border: '1px solid rgba(255,159,67,0.15)', borderRadius: 3, mb: 4 }}>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 14, color: '#ff9f43', mb: 2 }}>
              Case: Bright Horizons — the largest US childcare chain
            </Typography>
            <Typography sx={{ fontFamily: '"Libre Baskerville", Georgia, serif', fontSize: { xs: 14, md: 16 }, color: '#9499b0', lineHeight: 1.75, mb: 2 }}>
              Cameras were running. Staff were trained. Background checks were complete. Three workers still abused nine children across 18 months before it was caught. When the story broke, it wasn't one location that felt it — it was the brand.
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', borderTop: '1px solid rgba(255,255,255,0.06)', pt: 2, mt: 1 }}>
              The biggest chains are not immune. Size doesn't protect you. Visibility does.
            </Typography>
          </Box>

          <Grid container spacing={2.5}>
            {[
              { label: 'The viral incident', body: 'One clip, one parent, one tweet. It reaches every parent who has ever heard of your chain.' },
              { label: 'The class-action', body: 'Plaintiffs attorneys aggregate victims across locations into a single filing. One location\'s incident becomes a chain-wide lawsuit.' },
              { label: 'The enrollment collapse', body: 'Parents don\'t just leave the affected location. They pull children from every location they recognize the name on.' },
            ].map(({ label, body }) => (
              <Grid key={label} size={{ xs: 12, md: 4 }}>
                <Box sx={{ p: 3, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2, height: '100%' }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 14, color: '#f0f0f8', mb: 1 }}>{label}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.65 }}>{body}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Insurance ROI */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2 }}>
            <ShieldIcon sx={{ color: '#5ce1e6', fontSize: 20 }} />
            <Typography variant="overline" sx={{ color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em' }}>
              Insurance Impact
            </Typography>
          </Box>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.8rem' }, color: '#f0f0f8', letterSpacing: '-0.03em', mb: 3, lineHeight: 1.1 }}>
            Your insurance costs are already rising.<br />
            <Box component="span" sx={{ color: '#5ce1e6' }}>SMCLA is the reason they stop.</Box>
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: { xs: 15, md: 17 }, color: '#9499b0', lineHeight: 1.7, mb: 6, maxWidth: 680 }}>
            Abuse and molestation coverage is a separate, required endorsement in most states — not included in standard general liability. Mid-size childcare chains pay $5,000–$18,000 per year for combined insurance, and large operators pay $8,000–$60,000+. Since 2024, some carriers have increased A&M premiums by 300–1,000%. 62% of operators report difficulty finding affordable coverage at all.
          </Typography>

          <Grid container spacing={3} sx={{ mb: 5 }}>
            {[
              {
                label: 'The A&M problem',
                accent: '#ff9f43',
                body: 'Abuse and molestation coverage does not come with your GL policy. It must be added separately — and underwriters are tightening it fast. Facilities without documented safety systems are a harder sell every renewal cycle.',
              },
              {
                label: 'The SMCLA difference',
                accent: '#5ce1e6',
                body: 'SMCLA-monitored facilities have timestamped behavioral data, verified audit trails, and documented early-intervention history. That is a materially different risk profile from an unmonitored location — and underwriters can price it as such.',
              },
              {
                label: 'The ROI math',
                accent: '#8c52ff',
                body: 'A mid-size chain with 10 locations paying $10,000/year per facility in combined premiums spends $100,000 annually on insurance. A 15% A&M discount — actuarially justified by verified monitoring — pays for SMCLA many times over.',
              },
            ].map(({ label, accent, body }) => (
              <Grid key={label} size={{ xs: 12, md: 4 }}>
                <Box sx={{ p: 3, bgcolor: '#13131f', border: `1px solid ${accent}18`, borderTop: `3px solid ${accent}`, borderRadius: 2, height: '100%' }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 14, color: accent, mb: 1.5 }}>{label}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.65 }}>{body}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ p: { xs: 3, md: 4 }, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.15)', borderRadius: 3 }}>
            <Typography sx={{ fontFamily: '"Libre Baskerville", Georgia, serif', fontStyle: 'italic', fontSize: { xs: 14, md: 16 }, color: '#d0d0e0', lineHeight: 1.75 }}>
              "The cases that drive $1M+ settlements — repeated verbal abuse, targeted emotional cruelty, systematic physical harm — are currently invisible to your insurer. SMCLA is the first system that makes them visible before they become claims. That changes the actuarial picture entirely."
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Persona section */}
      <Box id="how-it-works" sx={{ py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            Who on your team benefits
          </Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
            Built for the whole leadership team
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.7, mb: 7, maxWidth: 560 }}>
            SMCLA is not a single-buyer purchase. Every leader with safety, risk, or operational responsibility gets something different — and something concrete.
          </Typography>

          <Stack spacing={2.5}>
            {personas.map(({ icon, role, benefit }) => (
              <Box key={role} sx={{ display: 'flex', gap: 3, p: 3, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3, alignItems: 'flex-start', transition: 'border-color 0.2s', '&:hover': { borderColor: 'rgba(92,225,230,0.2)' } }}>
                <Box sx={{ p: 1.25, bgcolor: 'rgba(92,225,230,0.08)', border: '1px solid rgba(92,225,230,0.15)', borderRadius: 2, flexShrink: 0 }}>
                  {icon}
                </Box>
                <Box>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 15, color: '#5ce1e6', mb: 0.75 }}>
                    {role}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.65 }}>
                    {benefit}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Privacy-first */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            Privacy & Compliance
          </Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
            You only ever see what matters.
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.7, mb: 7, maxWidth: 580 }}>
            Enterprise buyers ask about bulk footage storage, employee privacy, and GDPR. Here is exactly how SMCLA works — nothing is ambiguous.
          </Typography>

          <Grid container spacing={3}>
            {privacyPillars.map(({ icon, title, body }) => (
              <Grid key={title} size={{ xs: 12, sm: 6 }}>
                <Box sx={{ p: 3.5, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.1)', borderRadius: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                    {icon}
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 15, color: '#f0f0f8' }}>
                      {title}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7 }}>
                    {body}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Camera compatibility */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: 'inline-flex', p: 2, bgcolor: 'rgba(92,225,230,0.08)', border: '1px solid rgba(92,225,230,0.15)', borderRadius: 2, mb: 3 }}>
                <CameraAltIcon sx={{ color: '#5ce1e6', fontSize: 32 }} />
              </Box>
              <Typography variant="h3" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.6rem', md: '2rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
                Works with your existing cameras.
              </Typography>
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.7, mb: 3 }}>
                No rip-and-replace. No camera vendor negotiations. SMCLA installs as an intelligence layer on top of the IP cameras you already have. Setup per location typically takes 1–2 days.
              </Typography>
              <Stack spacing={1.5}>
                {[
                  'Compatible with 95%+ of commercial IP camera systems',
                  'Deployment per location: 1–2 days on-site',
                  'Our team handles state consent compliance during setup',
                  'No cameras at a location? We provide and install everything.',
                ].map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <CheckCircleIcon sx={{ color: '#5ce1e6', fontSize: 18, mt: 0.2, flexShrink: 0 }} />
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#d0d0e0', lineHeight: 1.6 }}>
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ p: 3, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3 }}>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 12, color: '#9499b0', letterSpacing: '0.08em', textTransform: 'uppercase', mb: 2.5 }}>
                  Supported camera brands
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {cameraLogos.map(brand => (
                    <Box key={brand} sx={{ px: 2, py: 1, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 1.5 }}>
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', fontWeight: 500 }}>{brand}</Typography>
                    </Box>
                  ))}
                  <Box sx={{ px: 2, py: 1, bgcolor: 'rgba(92,225,230,0.05)', border: '1px solid rgba(92,225,230,0.15)', borderRadius: 1.5 }}>
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#5ce1e6', fontWeight: 500 }}>+ many more</Typography>
                  </Box>
                </Box>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', my: 3 }} />
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.65 }}>
                  Not sure if your cameras are compatible? Share your camera model during the demo and we will confirm within 24 hours.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* What SMCLA detects */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            Detection Coverage
          </Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
            Physical abuse. Emotional abuse. Safety hazards.
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.7, mb: 7, maxWidth: 580 }}>
            SMCLA combines audio and video analysis to detect every category of harm — including the category that cameras alone have never been able to catch.
          </Typography>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ p: 3.5, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.15)', borderRadius: 3, height: '100%' }}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2.5 }}>
                  <ShieldIcon sx={{ color: '#5ce1e6', fontSize: 20 }} />
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 15, color: '#5ce1e6' }}>
                    Physical & Visual Detection
                  </Typography>
                </Box>
                <Stack spacing={1.5}>
                  {[
                    'Physical aggression and abnormal contact patterns',
                    'Rough handling and force anomalies',
                    'Unauthorized isolation of individual children',
                    'Safety hazards: falls, unsafe practices, environmental risks',
                    'Multi-session behavioral pattern matching across days and weeks',
                  ].map((item, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
                      <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#5ce1e6', mt: 0.7, flexShrink: 0 }} />
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.6 }}>{item}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ p: 3.5, bgcolor: '#13131f', border: '1px solid rgba(255,159,67,0.2)', borderRadius: 3, height: '100%' }}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2.5 }}>
                  <VolumeUpIcon sx={{ color: '#ff9f43', fontSize: 20 }} />
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 15, color: '#ff9f43' }}>
                    Emotional & Audio Detection
                  </Typography>
                </Box>
                <Stack spacing={1.5}>
                  {[
                    'Vocal tone anomalies: raised voices and shouting directed at children',
                    'Verbal threats and degrading language patterns',
                    'Emotional abuse: repeated humiliation of the same child across sessions',
                    'Behavioral deviation from each worker\'s own established baseline',
                    'Distress vocalizations from children — crying and sounds of distress',
                  ].map((item, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
                      <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#ff9f43', mt: 0.7, flexShrink: 0 }} />
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.6 }}>{item}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Cost math */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            Financial Case
          </Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.8rem' }, color: '#f0f0f8', letterSpacing: '-0.03em', mb: 3 }}>
            The math isn't close.
          </Typography>

          <Grid container spacing={3} sx={{ mb: 5 }}>
            {[
              {
                label: 'Human tape review',
                value: '$1,100–2,000',
                sub: 'per location / per week',
                detail: '~$18–25/hr × 60–80 hrs to review all rooms. Misses patterns. Has sick days.',
                color: '#ff6b6b',
                dim: true,
              },
              {
                label: 'SMCLA AI monitoring',
                value: 'Fraction of that',
                sub: 'real-time, 24/7, every room',
                detail: "Never misses a moment. Doesn't get tired. Detects patterns across days and weeks.",
                color: '#5ce1e6',
                dim: false,
              },
              {
                label: 'One settlement',
                value: '$200k–$2M+',
                sub: 'plus legal fees, enrollment loss, insurance spike',
                detail: 'Plus the class-action multiplier when plaintiffs aggregate across locations.',
                color: '#ff9f43',
                dim: true,
              },
            ].map(({ label, value, sub, detail, color, dim }) => (
              <Grid key={label} size={{ xs: 12, md: 4 }}>
                <Box sx={{ p: 3.5, bgcolor: '#13131f', border: `1px solid ${dim ? 'rgba(255,255,255,0.06)' : 'rgba(92,225,230,0.2)'}`, borderRadius: 3, height: '100%', opacity: dim ? 0.8 : 1 }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 12, color: '#9499b0', letterSpacing: '0.06em', textTransform: 'uppercase', mb: 1.5 }}>
                    {label}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.6rem', md: '2rem' }, color, lineHeight: 1.1, mb: 0.5 }}>
                    {value}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: '#9499b0', mb: 2 }}>
                    {sub}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.65 }}>
                    {detail}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ p: 3, bgcolor: 'rgba(92,225,230,0.04)', border: '1px solid rgba(92,225,230,0.15)', borderRadius: 2 }}>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7 }}>
              <Box component="span" sx={{ color: '#f0f0f8', fontWeight: 600 }}>For a 20-location chain: </Box>
              eliminating human tape review alone saves $1M+ per year. A single avoided settlement pays for SMCLA across your entire portfolio for over a decade.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Final CTA */}
      <Box sx={{ py: { xs: 10, md: 16 }, px: 3, textAlign: 'center' }}>
        <Container maxWidth="sm">
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '2rem', md: '3rem' }, color: '#f0f0f8', letterSpacing: '-0.03em', mb: 2, lineHeight: 1.1 }}>
            Built for your scale.
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: { xs: 16, md: 18 }, color: '#9499b0', lineHeight: 1.7, mb: 6 }}>
            We will scope SMCLA to your exact footprint — number of locations, existing camera brands, state compliance requirements, and multi-site dashboard configuration.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={scrollToContact}
            sx={{ px: 5, py: 1.75, fontWeight: 700, fontSize: 17, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, boxShadow: '0 0 32px rgba(92,225,230,0.25)', '&:hover': { bgcolor: '#4ecfd4' } }}
          >
            Request an Enterprise Demo →
          </Button>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: 'rgba(148,153,176,0.6)', mt: 2 }}>
            30-minute call. No commitment. Scoped to your locations.
          </Typography>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
